import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, IconButton, List, Paper, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Archive, Pin, PinOff } from "lucide-react";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

const pipelineDotColors = [
  "hsl(0 72% 51%)",
  "rgb(234 179 8)",
  "rgb(34 197 94)",
  "hsl(150 10% 46%)",
];

const PipelinesCard = ({ title, pipelines, onCreateClick, onArchive, showArchive = false, onPipelineClick, onTogglePin, maxVisible = 5, viewAllHref }) => {
  const navigate = useNavigate();
  const visiblePipelines = pipelines.slice(0, maxVisible);
  const hiddenCount = pipelines.length - visiblePipelines.length;

  return (
    <Paper sx={{ borderRadius: "0.75rem", border: "1px solid hsl(140 8% 88%)", overflow: "hidden", backgroundColor: "#ffffff" }} elevation={0}>
      <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid hsl(140 8% 88%)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>{title}</Typography>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 20,
            height: 20,
            px: "6px",
            borderRadius: "calc(-2px + 0.75rem)",
            backgroundColor: "hsl(217 91% 95%)",
            color: "hsl(217 70% 30%)",
            fontSize: 12,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            lineHeight: 1,
          }}
        >
          {pipelines.length}
        </Box>
      </Box>

      {pipelines.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: "hsl(150 10% 46%)", textAlign: "center", py: 4, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>No active pipelines</Typography>
      ) : (
        <List disablePadding>
          {visiblePipelines.map((pipeline, index) => (
            <Box
              key={pipeline.id}
              sx={{
                px: 2.5,
                py: 1.75,
                borderTop: index === 0 ? "none" : "1px solid hsl(140 8% 88%)",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                "& .archive-btn": { opacity: 0 },
                "& .pin-btn": { opacity: pipeline.pinned ? 1 : 0 },
                "&:hover .archive-btn": { opacity: 1 },
                "&:hover .pin-btn": { opacity: 1 },
                "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
              }}
              onClick={() => onPipelineClick?.(pipeline)}
            >
              {onTogglePin && (
                <IconButton
                  size="small"
                  className="pin-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    onTogglePin?.(pipeline);
                  }}
                  sx={{
                    p: 0.25,
                    transition: "opacity 0.2s ease, color 0.2s ease",
                    color: pipeline.pinned ? "hsl(150 30% 12%)" : "hsl(140 8% 82%)",
                    "&:hover": { color: "hsl(150 30% 12%)" },
                  }}
                >
                  {pipeline.pinned ? <PinOff size={14} strokeWidth={1.8} /> : <Pin size={14} strokeWidth={1.8} />}
                </IconButton>
              )}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: pipelineDotColors[index % pipelineDotColors.length],
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }} noWrap>
                  {pipeline?.title || "Untitled pipeline"}
                </Typography>
                <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }} noWrap>
                  {pipeline?.pipeline_type === "job_offer" ? pipeline?.company_name : pipeline?.title || "No role title"}
                </Typography>
              </Box>
              {pipeline?.assigned_users?.length > 0 && pipeline?.pipeline_type !== "personal" && (
                <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                  {pipeline.assigned_users.slice(0, 3).map((u) => (
                    <UserAvatar
                      key={u.id}
                      user={u}
                      size={24}
                      sx={{ ml: "-6px", "&:first-of-type": { ml: 0 } }}
                    />
                  ))}
                  {pipeline.assigned_users.length > 3 && (
                    <Typography sx={{ fontSize: 11, color: "hsl(150 10% 46%)", ml: 0.5, whiteSpace: "nowrap" }}>
                      +{pipeline.assigned_users.length - 3}
                    </Typography>
                  )}
                </Box>
              )}
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", flexShrink: 0 }}>{pipeline.profiles_count || 0}</Typography>
              {showArchive && (
                <IconButton
                  size="small"
                  className="archive-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    onArchive?.(pipeline.id);
                  }}
                  sx={{
                    ml: 0.5,
                    transition: "opacity 0.2s ease",
                    color: "hsl(150 10% 46%)",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.05)", color: "hsl(150 30% 12%)" },
                  }}
                >
                  <Archive size={14} strokeWidth={1.8} />
                </IconButton>
              )}
            </Box>
          ))}
        </List>
      )}

      {hiddenCount > 0 && viewAllHref && (
        <Box
          component="button"
          onClick={() => navigate(viewAllHref)}
          sx={{
            all: "unset",
            display: "block",
            width: "100%",
            textAlign: "center",
            py: 1.25,
            fontSize: 12,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: "hsl(150 38% 17%)",
            borderTop: "1px solid hsl(140 8% 88%)",
            cursor: "pointer",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
          }}
        >
          View all pipelines
        </Box>
      )}

      <Button
        fullWidth
        disableElevation
        startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
        onClick={onCreateClick}
        sx={{
          justifyContent: "flex-start",
          borderRadius: 0,
          px: 2.5,
          py: 1.5,
          textTransform: "none",
          fontSize: 14,
          color: "hsl(150 10% 46%)",
          borderTop: "1px solid hsl(140 8% 88%)",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.03)", color: "hsl(150 30% 12%)" },
        }}
      >
        New pipeline
      </Button>
    </Paper>
  );
};

export default PipelinesCard;
